import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight } from 'lucide-react';
import { catOf } from '@/components/WelcomeCalendar';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtWhen = (it) => {
  if (!it?.date) return '';
  const d = new Date(`${String(it.date).slice(0, 10)}T00:00:00`);
  const day = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(d);
  return it.detail ? `${day} · ${it.detail}` : day;
};

// Inner "next three events" list rendered INSIDE the single Calendar card
// (no separate card / border). Rows and the view link stop propagation so the
// surrounding Calendar card remains clickable to the full module.
export const CalendarPreview = ({ token }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    let on = true;
    const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const end = new Date(`${todayStr}T00:00:00`);
    end.setDate(end.getDate() + 120);
    const endStr = end.toLocaleDateString('en-CA');
    axios.get(`${API}/api/calendar/events`, {
      params: { start: todayStr, end: endStr },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => {
        if (!on) return;
        const evs = Array.isArray(r.data?.events) ? r.data.events : [];
        evs.sort((a, b) => String(a.date).localeCompare(String(b.date)));
        setItems(evs.slice(0, 3));
      })
      .catch(() => { if (on) setItems([]); });
    return () => { on = false; };
  }, [token]);

  const openEvent = (e, it) => { e.stopPropagation(); navigate(it?.route || '/calendar'); };
  const viewAll = (e) => { e.stopPropagation(); navigate('/calendar'); };

  return (
    <div className="mt-2 flex min-h-0 flex-1 flex-col border-t border-white/10 pt-2" data-testid="calendar-preview">
      {items === null && <div className="flex flex-1 items-center py-1.5 text-xs text-slate-400">Loading…</div>}
      {items !== null && items.length === 0 && (
        <div className="flex flex-1 items-center py-1.5 text-xs text-slate-400" data-testid="calendar-preview-empty">No upcoming events.</div>
      )}
      {items && items.length > 0 && (
        <ul className="flex min-h-0 flex-1 flex-col">
          {items.map((it, i) => {
            const c = catOf(it.source);
            return (
              <li key={`${it.id || i}-${it.date}`} className={`flex flex-1 items-stretch ${i < items.length - 1 ? 'border-b border-white/5' : ''}`}>
                <button onClick={(e) => openEvent(e, it)} data-testid={`calendar-preview-item-${i}`}
                  className="group/ev flex w-full items-center gap-2 rounded-md px-1.5 py-2 text-left outline-none transition-colors hover:bg-white/[0.07] focus-visible:bg-white/[0.07]">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-black/20" style={{ backgroundColor: c.color }} />
                  <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-white">{it.title}</span>
                  <span className="shrink-0 text-[11px] text-slate-400">{fmtWhen(it)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <button onClick={viewAll} data-testid="calendar-preview-viewall"
        className="mt-auto inline-flex items-center gap-0.5 self-start rounded-full px-1.5 pt-2 text-[11px] font-semibold text-[#8FC93A] outline-none transition-colors hover:text-white focus-visible:text-white">
        View Calendar <ChevronRight size={12} />
      </button>
    </div>
  );
};

export default CalendarPreview;
